<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <label class="font-semibold" :for="name">
        {{ label }}
        <span v-if="required" class="text-red-600">*</span>
      </label>

      <Multiselect
        v-model="parentInputValue"
        @clear="clearAll"
        :options="parentOptions"
        :multiple="false"
        :canClear="true"
        :label="'label'"
        :valueProp="'value'"
      />

      <p class="text-red-500" v-show="parentErrorMessage || parentMeta.valid">
        {{ parentErrorMessage }}
      </p>
    </div>

    <template v-if="parentInputValue">
      <div v-for="el in children" :key="el.parent" class="flex flex-col gap-1">
        <label class="font-semibold" :for="el.slug">{{ el.label }}</label>

        <Multiselect
          v-model="childrenValues[el.slug]"
          :options="el.children"
          :multiple="false"
          :canClear="true"
          :label="'label'"
          :valueProp="'value'"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import * as yup from 'yup';
import Multiselect from '@vueform/multiselect';
import { getNestedSelectValues } from '@/helpers/nestedSelectCache';

const { t } = useI18n();

const props = defineProps({
  value: { type: [String, Number], default: undefined },
  label: { type: String, required: true },
  name: { type: String, required: true },
  required: { type: Boolean, default: true },
  property: { type: Number, default: undefined },

  // edit-режим
  defaultParentMultiValues: { type: Array, default: undefined }, // select_values где parent=null
  defaultChildrenValues: { type: Array, default: undefined }, // select_values детей (parent != null)
});

const rules = yup.number().nullable().required(t('warning.REQUIRED_MESSAGE'));

const {
  meta: parentMeta,
  value: parentInputValue,
  errorMessage: parentErrorMessage,
  validate: parentValidate,
  handleChange: parentHandleChange,
} = useField(props.name, rules, { initialValue: props.value });

// ✅ как в рабочем
const parentOptions = ref([]); // [{value,label,slug,children,children_property}]
const parentOptionsCopy = ref([]); // оригинальные items из API (id,name,slug,children,...)
const children = ref([]); // [{parent,label,slug,children:[{value,label,slug}]}]
const childrenValues = ref({}); // { [parentSlug]: childId }

const clearAll = () => {
  parentHandleChange(null);
  parentInputValue.value = null;
  children.value = [];
  childrenValues.value = {};
};

const getParents = value => {
  children.value = [];
  childrenValues.value = {};

  if (!value) return;

  // parentOptionsCopy хранит оригинальные item, где id
  const detail = parentOptionsCopy.value.find(el => el.id === value);
  if (!detail || !detail.children) return;

  const children_ = detail.children.map(item => ({
    value: item.id,
    label: item.name,
    slug: item.slug,
  }));

  // инициализируем слот ребёнка для этой марки
  childrenValues.value[detail.slug] =
    props.defaultChildrenValues?.find(x => x.parent?.id == value)?.id ?? null;

  children.value = [
    {
      parent: value,
      label: `${detail.children_property?.name || 'Модель'} | ${detail.name}`,
      slug: detail.slug,
      children: children_,
    },
  ];
};

// ✅ как в рабочем: реагируем на смену марки
watch(parentInputValue, newVal => {
  getParents(newVal);
});

// ✅ полный сбор всех страниц (как в твоём рабочем компоненте)
const fetchAllOptions = async () => {
  parentOptions.value = [];
  parentOptionsCopy.value = [];

  const items = await getNestedSelectValues(props.property); // ✅ кэш + сверка 7 дней

  items.forEach(item => {
    parentOptions.value.push({
      value: item.id,
      label: item.name,
      slug: item.slug,
      children: item.children || [],
      children_property: item.children_property || {},
    });
    parentOptionsCopy.value.push(item);
  });
};

// ✅ выставляем дефолт (edit)
const initializeDefaultValues = () => {
  const parentId = props.defaultParentMultiValues?.find(item => !item.parent)?.id ?? null;
  if (parentId) parentInputValue.value = parentId;
};

// ✅ validateField — отдаём только ID (как тебе нужно)
const validateField = async () => {
  const parentValidatedData = await parentValidate();

  if (!parentInputValue.value) {
    return { value: undefined, valid: false, mode: 'multi', property: props.property };
  }

  const childIds = Object.values(childrenValues.value).filter(Boolean);

  return {
    value: [parentInputValue.value, ...childIds],
    valid: parentValidatedData.valid,
    mode: 'multi',
    property: props.property,
  };
};

defineExpose({ validateField });

onMounted(async () => {
  if (!props.property) return;
  await fetchAllOptions(); // ✅ тянем все страницы
  initializeDefaultValues(); // ✅ ставим дефолт марку
});
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
